import { Injectable } from '@angular/core';
import { HttpClient } from './http-client.service';
import * as model from '../../models';
import { SponsorCodeModel, SponsorCodeUsageModel } from '../../models';
import { Observable } from 'rxjs/Observable';
import { Response } from "@angular/http";
import { SponsorsFreeSeating } from "../../models/sponsors-freeSeating.model";
import { Store } from "@ngrx/store";
import { AppState } from "../../reducers";
import { SponsorActions } from "../../components/sponsors/sponsor.actions";

export interface QuerySponsorSearch {
	name: string
	email: string
	phone: string
	active: boolean
}

export interface QuerySponsorCodeSearch {
	code: string
	bookable: boolean
}


@Injectable()
export class SponsorApi {
	private sponsorURL = '/api/sponsoring/v1/sponsors/';
	private sponsorCodeURL = '/api/sponsoring/v1/sponsorCodes/';
	private sponsorCodeUsageURL = '/api/sponsoring/v1/sponsorCodes/codeUsages/'


	constructor(private httpClient: HttpClient, private store: Store<AppState>,
				private sponsorActions: SponsorActions,
	) {
	}

	// Sponsor
	public createSponsor(sponsor: model.Sponsor): Observable<model.Sponsor> {
		let body = JSON.stringify(sponsor)
		return this.httpClient.post(this.sponsorURL, body)
			.map(response => response.json())
	}

	public getSponsors(): Observable<model.Sponsor[]> {
		return this.httpClient.get(this.sponsorURL + "search/findAll")
			.map(response => response.json()._embedded.sponsors)
	}


	public loadSponsor(sponsorId: string): Observable<model.Sponsor> {
		return this.httpClient.get(this.sponsorURL + sponsorId)
			.map(response => <model.Sponsor>response.json())
	}

	public updateSponsor(url: string, sponsor: model.Sponsor): Observable<model.Sponsor> {
		let body = JSON.stringify(sponsor);
		return this.httpClient.patch(url, body)
			.map(response => <model.Sponsor>response.json())
	}

	// Sponsor codes
	public getCodesForSponsor(sponsorId: string): Observable<model.SponsorCodeModel[]> {
		return this.httpClient.get(this.sponsorCodeURL + 'search/findBySponsorId?sponsorId=' + sponsorId)
			.map(response => response.json())
	}

	public createSponsorCode(sponsorCode: SponsorCodeModel): Observable<SponsorCodeModel> {
		let body = JSON.stringify(sponsorCode)
		return this.httpClient.post(this.sponsorCodeURL, body)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public updateSponsorCode(sponsorCode: SponsorCodeModel): Observable<SponsorCodeModel> {
		let body = JSON.stringify(sponsorCode)
		return this.httpClient.patch(this.sponsorCodeURL + sponsorCode.id, body)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public getSponsorCodeById(sponsorCodeId: string): Observable<SponsorCodeModel> {
		return this.httpClient.get(this.sponsorCodeURL + sponsorCodeId)
			.map(response => response.json())
	}

	// Code Usages
	public getCodeUsagesForSponsorCodeId(sponsorCodeId: string): Observable<SponsorCodeUsageModel[]> {
		return this.httpClient.get(this.sponsorCodeUsageURL + 'search/findBySponsorCodeId?sponsorCodeId=' + sponsorCodeId)
			.map(response => response.json())
	}

	public createSponsorCodeUsage(sponsorCodeUsage: SponsorCodeUsageModel): Observable<SponsorCodeUsageModel> {
		let body = JSON.stringify(sponsorCodeUsage)
		return this.httpClient.post(this.sponsorCodeUsageURL, body)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public copySponsorCodeUsage(codeUsageId: number, eventUuid: string): Observable<{success: boolean, message: string | null}> {
		return this.httpClient.post(this.sponsorCodeUsageURL + codeUsageId + '/copy?eventUuid=' + eventUuid)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public updateSponsorCodeUsage(sponsorCodeUsage: SponsorCodeUsageModel): Observable<SponsorCodeUsageModel> {
		let body = JSON.stringify(sponsorCodeUsage)
		return this.httpClient.patch(this.sponsorCodeUsageURL + sponsorCodeUsage.id, body)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public getSponsorCodeUsageById(sponsorCodeUsageId: string): Observable<SponsorCodeUsageModel> {
		return this.httpClient.get(this.sponsorCodeUsageURL + sponsorCodeUsageId)
			.map(response => response.json())
	}

	// Code Usage Seats
	public saveSeatsForCodeUsage(codeUsageId: string, seatIdentifiers: string[]): Observable<string[]> {
		let body = JSON.stringify(seatIdentifiers)
		return this.httpClient.post(this.sponsorCodeUsageURL + codeUsageId + "/seats", body)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public releaseSeatsFromCodeUsage(codeUsageId: string, seatIdentifiers: string[]): Observable<string[]> {
		let body = JSON.stringify(seatIdentifiers)
		return this.httpClient.post(this.sponsorCodeUsageURL + codeUsageId + "/seats/release", body)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public getSeatsByCodeUsageId(codeUsageId: string): Observable<string[]> {
		return this.httpClient.get(this.sponsorCodeUsageURL + codeUsageId + "/seats")
			.map(response => response.json())
	}

	// Code Usage FreeSeating
	public saveFreeSeatingForCodeUsage(codeUsageId: string, freeSeating: SponsorsFreeSeating): Observable<string[]> {
		let body = JSON.stringify(freeSeating)
		return this.httpClient.post(this.sponsorCodeUsageURL + codeUsageId + "/freeSeatings", body)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public deleteFreeSeatingById(codeUsageId: string, freeSeating: SponsorsFreeSeating): Observable<string[]> {
		return this.httpClient.delete(this.sponsorCodeUsageURL + codeUsageId + "/freeSeatings/" + freeSeating.id)
			.map(response => response.json())
			.catch(this.handleError)
	}

	public getFreeSeatingsByCodeUsageId(codeUsageId: string): Observable<SponsorsFreeSeating[]> {
		return this.httpClient.get(this.sponsorCodeUsageURL + codeUsageId + "/freeSeatings")
			.map(response => response.json())
	}

	// Other TODO: Delete if not needed
	public getReservationsByEventUuid(eventUuid: string): Observable<{ seats: string[], freeSeatings: SponsorsFreeSeating[] }> {
		const sObs = this.httpClient.get(this.sponsorCodeUsageURL + "/seatsByEventUuid/" + eventUuid)
			.map(response => response.json())
		const fsObs = this.httpClient.get(this.sponsorCodeUsageURL + "/freeSeatingsByEventUuid/" + eventUuid)
			.map(response => response.json())

		return Observable.combineLatest(sObs, fsObs, (s, fs) => {
			return {seats: s, freeSeatings: fs}
		});
	}

	public fetchReservationsByEventUuidIntoStore(eventUuid: string): void {
		const sObs = this.httpClient.get(this.sponsorCodeUsageURL + "/seatsByEventUuid/" + eventUuid)
			.map(response => response.json())
		const fsObs = this.httpClient.get(this.sponsorCodeUsageURL + "/freeSeatingsByEventUuid/" + eventUuid)
			.map(response => response.json())

		Observable.combineLatest(sObs, fsObs, (s, fs) => {
			return {seats: s, freeSeatings: fs}
		}).subscribe(reservations => this.store.dispatch(this.sponsorActions.setReservationsByEventUuid(reservations)));
	}

	private handleError = (error: Response) => {
		if (error.status === 409) {
			return Observable.throw("409");
		} else {
			return Observable.throw(error.json());
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/services/webservice/sponsor-api.service.ts